/**
 * Dual Optimizely emitter: Web Experimentation data layer + FX mirror.
 *
 * Lives in a leaf module so exp-commerce / exp-bag / exp-behaviour can
 * dual-write without importing each other.
 *
 * Project JS cutover (Garage NA, Dynamite, UK): deploy this app first, confirm
 * one `window.optimizely` push per event and that FX is not duplicated, then
 * delete the event-emitting sections (6, 9, 10, 11, 12 and the commented
 * scroll handler). Keep utilities, locale, visitor attributes (`site_country` /
 * `site_locale` / `site_language`) and PDP helpers (`W_OPTLY.pdp`) — WebX
 * experiments use those for targeting, not as events. Watch metrics 24–48 h.
 */
import {trackFxEvent} from './optimizely-fx-events'

const stripNullish = (obj) => {
    if (!obj || typeof obj !== 'object') return undefined
    const cleaned = {}
    for (const [key, val] of Object.entries(obj)) {
        if (val !== undefined && val !== null) {
            cleaned[key] = val
        }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

/**
 * Push a custom event to the Optimizely Web Experimentation data layer and
 * mirror it into Feature Experimentation. WebX always fires; FX no-ops when
 * tracking is disabled.
 *
 * @param {Object} params
 * @param {string} params.eventName - Required Optimizely event name.
 * @param {Object} [params.tags] - Optional tags object (revenue, value).
 * @param {Object} [params.properties] - Optional properties object.
 */
export const trackOptimizelyEvent = ({eventName, tags, properties}) => {
    if (typeof window === 'undefined') return

    try {
        window['optimizely'] = window['optimizely'] || []

        const payload = {
            type: 'event',
            eventName
        }

        const cleanTags = stripNullish(tags)
        if (cleanTags) {
            payload.tags = cleanTags
        }

        const cleanProps = stripNullish(properties)
        if (cleanProps) {
            payload.properties = cleanProps
        }

        window['optimizely'].push(payload)
    } catch (error) {
        console.error('[Optimizely Web Exp] Failed to track event:', eventName, error)
    }

    // Mirror into Feature Experimentation as a conversion event. Never throws.
    trackFxEvent(eventName, tags, properties)
}
